import { Box, Typography } from "@mui/material";
import { CardDTO, PlayerInfo, TeamId, toCardCode } from "../interface/GameTypes";
import { PlayingCardFace } from "./PlayingCardFace";

const BASE_POINTS: Record<string, number> = {
  Ace: 11, Ten: 10, King: 3, Queen: 2, Jack: 1, Nine: 0,
};
const cardPoints = (card: CardDTO, trumpSuit: string | null): number => {
  if (trumpSuit && card.suit === trumpSuit) {
    if (card.rank === "Jack") return 20;
    if (card.rank === "Nine") return 14;
  }
  return BASE_POINTS[card.rank] ?? 0;
};

interface PlayedCard {
  playerIndex: number;
  card: CardDTO;
}

interface Props {
  round: PlayedCard[];
  players: PlayerInfo[];
  currentTurnIndex: number | null;
  roundLeaderIndex: number | null;
  totalPlayers: number;
  trumpSuit: string | null;
  roundsPlayed: number;
  totalRounds: number;
  callerIndex?: number | null;
  teamARoundWins: number;
  teamBRoundWins: number;
  /** "sidebar" = right panel on desktop; "inline" = compact below-table strip */
  layout?: "sidebar" | "inline";
  /** Latest card to the trick — slam-in motion for this seat’s badge. */
  slamSeatIndex?: number | null;
  slamMotionTick?: number;
  /** Increments when a trump card hits the trick — gold ripple on trump badges. */
  trumpMotionTick?: number;
}

const PlayedCardVisual = ({
  card,
  trumpSuit,
  compact = false,
}: {
  card?: CardDTO;
  trumpSuit: string | null;
  compact?: boolean;
}) => {
  const hasCard = Boolean(card);
  const isTrump = Boolean(card && card.suit === trumpSuit);
  const code = card ? toCardCode(card) : null;

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 1.4,
        p: compact ? 0.08 : 0.14,
        backgroundColor: isTrump ? "rgba(255,215,0,0.12)" : "transparent",
        border: isTrump ? "1px solid rgba(255,215,0,0.5)" : "1px solid transparent",
        boxShadow: isTrump ? "0 0 0 1px rgba(255,215,0,0.2), 0 0 8px rgba(255,215,0,0.22)" : "none",
      }}
    >
      {hasCard && code ? (
        <PlayingCardFace
          rankCode={code.value}
          suitCode={code.suit}
          widthPx={compact ? 40 : 46}
          sx={{
            borderRadius: 1.25,
            boxShadow: isTrump ? "0 5px 12px rgba(255,215,0,0.28)" : "0 5px 10px rgba(0,0,0,0.22)",
            border: isTrump ? "1px solid rgba(255,215,0,0.66)" : "1px solid rgba(148,163,184,0.45)",
          }}
        />
      ) : (
        <Box
          sx={{
            width: compact ? 40 : 46,
            height: compact ? 57 : 66,
            borderRadius: 1.25,
            border: "1px dashed rgba(255,255,255,0.18)",
            backgroundColor: "rgba(255,255,255,0.04)",
          }}
        />
      )}
    </Box>
  );
};

export const RoundArea = ({
  round,
  players,
  currentTurnIndex,
  roundLeaderIndex,
  totalPlayers,
  trumpSuit,
  roundsPlayed,
  totalRounds,
  callerIndex,
  teamARoundWins,
  teamBRoundWins,
  layout = "sidebar",
  slamSeatIndex = null,
  slamMotionTick = 0,
  trumpMotionTick = 0,
}: Props) => {
  if (totalPlayers === 0) return null;

  const leader = roundLeaderIndex ?? 0;
  const turnOrder = Array.from({ length: totalPlayers }, (_, i) => (leader + i) % totalPlayers);

  const isSidebar = layout === "sidebar";

  return (
    <Box
      sx={{
        backgroundColor: "rgba(0,0,0,0.6)",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 2,
        p: isSidebar ? 2 : 1.5,
        color: "white",
        width: "100%",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography
          sx={{ color: "rgba(255,255,255,0.7)", letterSpacing: 1.5, fontSize: "0.85rem", fontWeight: "bold" }}
        >
          CURRENT ROUND
        </Typography>
        <Typography
          sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem", fontWeight: "bold" }}
        >
          {roundsPlayed + 1} / {totalRounds}
        </Typography>
      </Box>

      {/* Round points (live tally of cards played so far) */}
      {round.length > 0 && (
        <Box sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "baseline",
          gap: 0.75,
          mb: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: "rgba(255,255,255,0.08)",
        }}>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
            Pot:
          </Typography>
          <Typography sx={{ color: "#fff", fontSize: "1.1rem", fontWeight: "bold" }}>
            {round.reduce((sum, pc) => sum + cardPoints(pc.card, trumpSuit), 0)}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.85rem" }}>
            pts
          </Typography>
        </Box>
      )}

      {/* Round wins */}
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", alignItems: "center", mb: 1.5, px: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#4caf50" }} />
          <Typography sx={{ color: "#4caf50", fontSize: "0.9rem", fontWeight: "bold" }}>
            A: {teamARoundWins}
          </Typography>
        </Box>
        <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", px: 0.75 }}>
          rounds won
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifySelf: "end" }}>
          <Typography sx={{ color: "#ffc107", fontSize: "0.9rem", fontWeight: "bold" }}>
            B: {teamBRoundWins}
          </Typography>
          <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#ffc107" }} />
        </Box>
      </Box>

      {/* Per-player rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {turnOrder.map((seatIdx) => {
          const played = round.find((pc) => pc.playerIndex === seatIdx);
          const player = players.find((p) => p.seatIndex === seatIdx);
          const isLeader = seatIdx === leader;
          const isCaller = seatIdx === callerIndex;
          const isNext = seatIdx === currentTurnIndex && !played;
          const hasPlayed = !!played;

          return (
            <Box
              key={seatIdx}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(0, 1fr) auto",
                alignItems: "center",
                columnGap: 1,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                backgroundColor: isNext
                  ? "rgba(255,215,0,0.12)"
                  : hasPlayed
                  ? "rgba(255,255,255,0.04)"
                  : "transparent",
                border: isNext
                  ? "1px solid rgba(255,215,0,0.4)"
                  : "1px solid transparent",
                transition: "all 0.2s",
              }}
            >
              {/* Left: indicator + name + team */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, overflow: "hidden" }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    flexShrink: 0,
                    backgroundColor: isNext
                      ? "#FFD700"
                      : hasPlayed
                      ? "#4caf50"
                      : "rgba(255,255,255,0.3)",
                  }}
                />
                <Typography
                  sx={{
                    color: isNext
                      ? "#FFD700"
                      : hasPlayed
                      ? "#fff"
                      : "rgba(255,255,255,0.5)",
                    fontWeight: isNext ? "bold" : 500,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontSize: "0.95rem",
                  }}
                >
                  {player?.name ?? `Seat ${seatIdx}`}
                  {player?.teamId && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        color: player.teamId === TeamId.A ? "#66bb6a" : "#ffca28",
                      }}
                    >
                      [{player.teamId}]
                    </Box>
                  )}
                  {isCaller && (
                    <Box component="span" sx={{ ml: 0.5, color: "#ffca28", fontSize: "0.8rem", fontWeight: "bold" }}>
                      (Atta)
                    </Box>
                  )}
                  {isLeader && !isCaller && (
                    <Box component="span" sx={{ ml: 0.5, color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
                      (led)
                    </Box>
                  )}
                </Typography>
              </Box>

              {/* Right: fixed slot (card placeholder + points/status) so rows never jump */}
              <Box
                key={
                  played && slamMotionTick > 0 && slamSeatIndex === seatIdx
                    ? `slam-${slamMotionTick}-${seatIdx}`
                    : played && trumpMotionTick > 0 && trumpSuit && played.card.suit === trumpSuit
                      ? `trump-${trumpMotionTick}-${seatIdx}`
                      : `slot-${seatIdx}`
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  justifySelf: "end",
                  minWidth: 92,
                  justifyContent: "flex-end",
                  ...(played && slamMotionTick > 0 && slamSeatIndex === seatIdx
                    ? {
                        animation: "addataSlamCard 0.38s cubic-bezier(0.34, 1.56, 0.64, 1)",
                        "@keyframes addataSlamCard": {
                          "0%": { opacity: 0, transform: "scale(0.35) translateY(14px)" },
                          "100%": { opacity: 1, transform: "scale(1) translateY(0)" },
                        },
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                      }
                    : {}),
                  ...(played &&
                  slamSeatIndex !== seatIdx &&
                  trumpMotionTick > 0 &&
                  trumpSuit &&
                  played.card.suit === trumpSuit
                    ? {
                        animation: "addataTrumpRippleBadge 0.52s ease-out",
                        "@keyframes addataTrumpRippleBadge": {
                          "0%, 100%": { filter: "brightness(1)" },
                          "40%": {
                            filter: "brightness(1.25) drop-shadow(0 0 10px rgba(255,215,0,0.65))",
                          },
                        },
                        "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                      }
                    : {}),
                }}
              >
                <PlayedCardVisual card={played?.card} trumpSuit={trumpSuit} compact={isSidebar} />
                <Typography
                  sx={{
                    color: played
                      ? played.card.suit === trumpSuit
                        ? "#ffd54f"
                        : "#fff"
                      : isNext
                        ? "#FFD700"
                        : "rgba(255,255,255,0.35)",
                    fontSize: "0.9rem",
                    fontWeight: played ? "bold" : 500,
                    fontStyle: played || isNext ? "normal" : "italic",
                    minWidth: 34,
                    textAlign: "right",
                  }}
                >
                  {played ? cardPoints(played.card, trumpSuit) : isNext ? "playing…" : "waiting"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
